/**
 * **arcade テーマの「盤に置かれているもの」**（Issue #523）。
 *
 * テーマが返すのは**見た目だけ**（`stage/ObjectLayer.js` の契約）。板の**上面の絵**を 2D canvas で
 * 描く `draw` と、板の厚み・浮き・回しを返す純関数で、**どこに置かれるかも three も知らない**。
 *
 * | | 厚み | 浮き | 影 |
 * |---|---|---|---|
 * | カード | 1 枚ぶん | 少し浮かせる（盤から離れて見える） | あり |
 * | 山 | **枚数ぶん**（デッキが厚く見える） | 0（盤に置いてある） | あり |
 *
 * **向きは持たない** —— 盤の敷き方の分は Field（`cardAngle`）、守備表示の 90° はカードの状態で、
 * どちらも置き方＝器の仕事（`stage/ObjectLayer.js`）。
 *
 * 絵の寸法は**ビットマップの幅に対する比**で書く（`res` を上げても見た目が変わらない）。
 * 元は CSS（`arcade.css` の `.de-card`）で、カードの幅 130px のときの px 値から比にしてある。
 */
import { videoSourceRect } from '../../core/field';
import { withDefaults } from '../../core/theme';
import { drawCover, fitText, glowStroke, quadPath, roundRectPath } from '../../canvas/paint';
import { PLAYER_COLORS } from '../../core/hud';
import { CARD_ASPECT } from '../../core/field/zones';
import { decorOf } from '../../core/decor';

/**
 * **触れるもの**（Issue #523）。`drawObjects` も canvas なので、CSS 変数ではなく
 * **`style` の値をそのまま読む**（宣言と保存は HUD と同じ 1 つ）。
 */
export const OBJECT_SCHEMA = Object.freeze({
  cardRadius: { type: 'number', label: '角丸', min: 0, max: 0.2, step: 0.005, part: 'objects:card' },
  cardBorder: { type: 'number', label: '縁の太さ', min: 0, max: 0.06, step: 0.002, part: 'objects:card' },
  cardEdgeColor: { type: 'text', label: '縁の色', part: 'objects:card' },
  cardBg: { type: 'color', label: '画像が無いときの地', part: 'objects:card' },
  cardBackA: { type: 'color', label: '裏面のグラデ（明）', part: 'objects:card' },
  cardBackB: { type: 'color', label: '裏面のグラデ（暗）', part: 'objects:card' },
  cardLift: { type: 'number', label: '浮き', min: 0, max: 40, step: 0.5, part: 'objects:card' },
  cardThickness: { type: 'number', label: '厚み', min: 0, max: 12, step: 0.1, part: 'objects:card' },
  cardEdgeTone: { type: 'color', label: '小口の色', part: 'objects:card' },
  badgeBg: { type: 'text', label: '枚数の札の地', part: 'objects:pile' },
  badgeFont: { type: 'number', label: '枚数の大きさ', min: 0.05, max: 0.4, step: 0.005, part: 'objects:pile' },
  pilePerCard: { type: 'number', label: '1 枚あたりの厚み', min: 0, max: 4, step: 0.05, part: 'objects:pile' },
  pileMax: { type: 'number', label: '厚みの上限', min: 0, max: 120, step: 1, part: 'objects:pile' },
  handFrameDark: { type: 'color', label: '実写の手札の枠', part: 'objects:liveHand' },
  handFrameLight: { type: 'color', label: '実写の手札の内線', part: 'objects:liveHand' },
});

/**
 * **飾りを名乗る部品**（Issue #523）。盤の縁は「太さ ＋ にじみ」だけなので、
 * 固有の `frameWidth` / `frameGlow` をやめて `core/decor` の語彙に寄せた。
 */
export const OBJECT_PARTS = Object.freeze([
  Object.freeze({ part: 'objects:frame', decor: ['stroke', 'glow'] }),
]);

/** 既定値（**ここが唯一の真実**） */
export const OBJECT_STYLE = Object.freeze({
  cardRadius: 0.031, cardBorder: 0.0154, cardEdgeColor: 'rgba(0,0,0,0.5)',
  cardBg: '#1b2233', cardBackA: '#3a2a5a', cardBackB: '#1b1430',
  cardLift: 2.5, cardThickness: 1.6, cardEdgeTone: '#e8e4d8',
  badgeBg: 'rgba(0,0,0,0.7)', badgeFont: 0.169,
  pilePerCard: 0.8, pileMax: 40,
  handFrameDark: '#1f1f1f', handFrameLight: '#ffffff',
  // 盤の縁（太さ 12 の線に、一番外 48 のにじみを 5 枚）
  'dc/objects:frame/strokeW': 12,
  'dc/objects:frame/glowW': 48,
  'dc/objects:frame/glowA': 0.1,
  'dc/objects:frame/glowN': 5,
});

const objStyle = withDefaults(() => [OBJECT_STYLE]);

/** カードの幅に対する比（`OBJECT_STYLE` から。名前の大きさと札の余白は固定） */
const R = Object.freeze({ name: 0.154, pad: 0.046 });

/** カード 1 枚の厚み（平面のピクセル） */
export const CARD_THICKNESS = OBJECT_STYLE.cardThickness;
/** 山の 1 枚あたりの厚み */
export const PILE_PER_CARD = OBJECT_STYLE.pilePerCard;

/**
 * 板の上面（カード 1 枚の絵）を描く。`art` / `back` は**読み込み済みの画像**（読み込みはページの仕事）。
 *
 * **裏面はそのデッキのスリーブ**（Issue #523。`scene.cards.sleeve(player)`）。
 * 取れなければ地のグラデーションに落ちる。
 */
function paintCard(ctx, { size }, { card, art, back, name, badge, v = OBJECT_STYLE }) {
  const { w, h } = size;
  const r = w * v.cardRadius;
  const b = w * v.cardBorder;
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, w, h);
  ctx.save();
  roundRectPath(ctx, 0, 0, w, h, r);
  ctx.clip();

  if (card?.faceUp && art) {
    drawCover(ctx, art, w, h);
  } else if (card?.faceUp) {
    ctx.fillStyle = v.cardBg;
    ctx.fillRect(0, 0, w, h);
    if (name) {
      ctx.fillStyle = '#fff';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      fitText(ctx, name, w - b * 4, w * R.name);
      ctx.fillText(name, w / 2, h / 2);
    }
  } else if (back) {
    // **裏面はそのデッキのスリーブ**
    drawCover(ctx, back, w, h);
  } else {
    // スリーブが取れないときの地（CSS の linear-gradient(140deg, #3a2a5a, #1b1430) と同じ向き）
    const g = ctx.createLinearGradient?.(0, 0, w, h);
    if (g) { g.addColorStop(0, v.cardBackA); g.addColorStop(1, v.cardBackB); ctx.fillStyle = g; } else { ctx.fillStyle = v.cardBackB; }
    ctx.fillRect(0, 0, w, h);
    ctx.strokeStyle = 'rgba(255,255,255,0.35)';
    ctx.lineWidth = b * 2;
    roundRectPath(ctx, b, b, w - b * 2, h - b * 2, r);
    ctx.stroke();
  }

  if (badge != null) {
    const fs = w * v.badgeFont;
    const pad = w * R.pad;
    ctx.font = `700 ${fs}px sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const text = String(badge);
    const bw = (ctx.measureText?.(text)?.width ?? fs * text.length * 0.6) + pad * 2;
    const bh = fs * 1.3;
    const bx = w - bw - b; const by = h - bh - b;
    ctx.fillStyle = v.badgeBg;
    roundRectPath(ctx, bx, by, bw, bh, bh / 2);
    ctx.fill();
    ctx.fillStyle = '#fff';
    ctx.fillText(text, bx + bw / 2, by + bh / 2);
  }
  ctx.restore();

  // 縁（CSS の box-shadow 0 0 0 2px rgba(0,0,0,.5)）
  ctx.strokeStyle = v.cardEdgeColor;
  ctx.lineWidth = b * 2;
  roundRectPath(ctx, b, b, w - b * 2, h - b * 2, r);
  ctx.stroke();
}

/** 場のカード 1 枚 */
export function card({ item, scene, style }) {
  const v = objStyle(style);
  const c = item.card;
  const art = c.faceUp && c.code ? scene?.cards?.image?.(c.code) : null;
  const back = c.faceUp ? null : scene?.cards?.sleeve?.(item.player);
  const name = c.code ? (scene?.cards?.name?.(c.code) ?? String(c.code)) : '';
  return {
    lift: v.cardLift,
    thickness: v.cardThickness,
    edgeColor: v.cardEdgeTone,
    // 画像は**後から届く**（読み込みはページ）ので、届いたら描き直すために deps に入れる
    deps: [art, back],
    draw: (ctx, env) => paintCard(ctx, env, { card: c, art, back, name, badge: null, v }),
  };
}

/** 山（デッキ・墓地・除外・EX・手札）。**一番上の 1 枚と枚数**、厚みは枚数ぶん */
export function pile({ item, scene, style }) {
  const v = objStyle(style);
  const top = item.top;
  const art = top?.faceUp && top.code ? scene?.cards?.image?.(top.code) : null;
  const back = top?.faceUp ? null : scene?.cards?.sleeve?.(item.player);
  const name = top?.code ? (scene?.cards?.name?.(top.code) ?? String(top.code)) : '';
  return {
    lift: 0,
    thickness: Math.min(v.pileMax, Math.max(1, item.count) * v.pilePerCard),
    edgeColor: v.cardEdgeTone,
    deps: [art, back],
    draw: (ctx, env) => paintCard(ctx, env, {
      card: top ?? { faceUp: false },
      art,
      back,
      name,
      badge: item.count > 1 ? item.count : null,
      v,
    }),
  };
}

/* ------------------------------------------------------------------ *
 *  盤の縁（舞台のピクセルに置く。遠近で太さが潰れない）
 * ------------------------------------------------------------------ */

/**
 * **盤の縁をターンプレイヤーの色で光らせる**（旧 `/fx` の `FieldFrame` と同じ見え方）。
 *
 * 盤の 4 点（`view.outline`）に沿って**舞台のピクセル**で描くので、遠近で太さが潰れない。
 * **デュエル開始前（ターン 0）は出さない。**
 */
export function frame({ item, scene, view, canvas, style }) {
  const outline = view?.outline;
  if (!outline || !item.turn || !canvas) return null;
  const color = scene?.players?.[item.player]?.color ?? PLAYER_COLORS[item.player] ?? PLAYER_COLORS[0];
  const ink = decorOf(style, 'objects:frame');
  // 光がにじむぶん外に余白を取った箱（舞台いっぱいの板を作らない）。にじみの一番外ぶん
  const pad = ink.glowW;
  const xs = outline.map((p) => p[0]); const ys = outline.map((p) => p[1]);
  const x = Math.max(0, Math.min(...xs) - pad); const y = Math.max(0, Math.min(...ys) - pad);
  const box = {
    x,
    y,
    w: Math.min(canvas.w, Math.max(...xs) + pad) - x,
    h: Math.min(canvas.h, Math.max(...ys) + pad) - y,
  };
  if (!(box.w > 0) || !(box.h > 0)) return null;
  const pts = outline.map(([px, py]) => [px - x, py - y]);
  return {
    box,
    bitmap: { w: Math.round(box.w), h: Math.round(box.h) },
    deps: [color, ink.strokeW, ink.glowW, ink.glowA, ink.glowN, ...outline.flat()],
    draw: (ctx, { size }) => {
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, size.w, size.h);
      ctx.lineJoin = 'round';
      // **にじみは影ではなく重ね塗り**（`canvas/paint::glowStroke`。区画の縁と同じ道具）
      glowStroke(ctx, (c) => quadPath(c, pts), { ...ink, color });
    },
  };
}

/* ------------------------------------------------------------------ *
 *  実写っぽい手札（舞台のピクセルに置く。遠近は付かない）
 * ------------------------------------------------------------------ */

/** 舞台の下（P1）と上（P2）に置く箱（舞台の大きさに対する比） */
const HAND_BOX = { w: 0.42, h: 0.1, margin: 0.015 };

/**
 * 合成フィールド動画の**両端の帯**（平面の外に落ちている手札）を切り出す。
 *
 * 合成は `hand2 / field2 / field1 / hand1` の縦積みで、マットは 1 枚 `plane.h / 2`。
 * 上下の帯の高さは同じ前提（`utils/defaultOverlayZones.js::configGuessIssue` が先に弾く）。
 */
export function handBand(size, plane, player) {
  if (!(size?.w > 0) || !(size?.h > 0) || !(plane?.h > 0)) return null;
  const rest = size.h - plane.h;      // 平面（マット 2 枚）の外＝上下の帯
  if (!(rest > 1)) return null;
  const h = rest / 2;
  return { x: 0, y: player === 1 ? 0 : size.h - h, w: size.w, h };
}

export function liveHand({ item, scene, view, canvas, style }) {
  const v = objStyle(style);
  const el = scene?.video?.el ?? null;
  const band = handBand(scene?.video?.size, view?.plane, item.player);
  // 要素が帯を持っていない（描く側は平面に入る部分だけ渡す）なら出さない。要素の画素へは core が写す（Issue #808）
  const src = band ? videoSourceRect(scene?.video, band) : null;
  if (!el || !band || !src || !canvas) return null;
  const boxW = canvas.w * HAND_BOX.w;
  const boxH = Math.min(canvas.h * HAND_BOX.h, (boxW * band.h) / band.w);
  const margin = canvas.h * HAND_BOX.margin;
  const box = {
    x: (canvas.w - boxW) / 2,
    y: item.player === 1 ? margin : canvas.h - margin - boxH,
    w: boxW,
    h: boxH,
  };
  const bitmap = { w: Math.round(band.w), h: Math.round(band.h) };
  const k = bitmap.w / boxW;   // 舞台のピクセル → ビットマップのピクセル
  return {
    box,
    bitmap,
    // 動画はフレームごとに変わる（**`frameMs` を読んでよいのは動画を描くものだけ**）
    deps: [el, scene?.frameMs, src.x, src.y, src.w, src.h],
    draw: (ctx) => {
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, bitmap.w, bitmap.h);
      ctx.fillStyle = '#05070e';
      ctx.fillRect(0, 0, bitmap.w, bitmap.h);
      if (!(typeof el.readyState === 'number' && el.readyState < 2)) {
        ctx.save();
        // P2（奥）の帯は 180° 回って積まれているので、写すときに戻す
        if (item.player === 1) ctx.setTransform(-1, 0, 0, -1, bitmap.w, bitmap.h);
        ctx.drawImage(el, src.x, src.y, src.w, src.h, 0, 0, bitmap.w, bitmap.h);
        ctx.restore();
      }
      // 枠（CSS の border 5px + inset 2px）
      ctx.lineWidth = 2 * k;
      ctx.strokeStyle = v.handFrameLight;
      ctx.strokeRect(6 * k, 6 * k, bitmap.w - 12 * k, bitmap.h - 12 * k);
      ctx.lineWidth = 5 * k;
      ctx.strokeStyle = v.handFrameDark;
      ctx.strokeRect(2.5 * k, 2.5 * k, bitmap.w - 5 * k, bitmap.h - 5 * k);
      // 見出し
      const fs = 46 * k;
      ctx.font = `900 italic ${fs}px sans-serif`;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';
      ctx.lineJoin = 'round';
      ctx.lineWidth = 7 * k;
      ctx.strokeStyle = '#101010';
      ctx.strokeText?.(`${item.player + 1}P HAND`, 8 * k, 4 * k);
      ctx.fillStyle = '#fff';
      ctx.fillText(`${item.player + 1}P HAND`, 8 * k, 4 * k);
    },
  };
}

/**
 * **対象の印**（Issue #534）。演出が置いてオブジェクトレイヤーが生存を持つもの。
 *
 * `become_target` が置き、チェーンが解決したら `chain_solved` / `chain_end` / `new_turn` が消す。
 * 帯が終わっても残るので**その間ずっと出ている**（設計文書 §4）。
 * カードの中心に照準（回る外輪 ＋ 十字の目盛り）—— **回すのは器の仕事ではない**ので
 * ここは止まった 1 枚を描く（動かしたければ演出が `tweak` で触る）。
 */
function target({ size, style }) {
  const color = style.targetColor;
  return {
    lift: 2,
    thickness: 0,
    shadow: false,
    deps: [color, style.targetWidth],
    draw: (ctx, { size: px }) => {
      const k = px.w / Math.max(1, size.w);
      const cx = px.w / 2;
      const cy = px.h / 2;
      const r = Math.min(px.w, px.h) * 0.42;
      ctx.strokeStyle = color;
      ctx.lineWidth = style.targetWidth * k;
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.stroke();
      ctx.beginPath();
      [0, 90, 180, 270].forEach((deg) => {
        const a = (deg * Math.PI) / 180;
        ctx.moveTo(cx + Math.cos(a) * r * 0.55, cy + Math.sin(a) * r * 0.55);
        ctx.lineTo(cx + Math.cos(a) * r * 1.25, cy + Math.sin(a) * r * 1.25);
      });
      ctx.stroke();
    },
  };
}

/** 印の宣言（`core/mark` の名前で引かれる） */
export const MARK_SCHEMA = Object.freeze({
  targetColor: { type: 'color', label: '対象の印の色', part: 'objects:target' },
  targetWidth: { type: 'number', label: '対象の印の太さ', min: 1, max: 20, step: 1, part: 'objects:target' },
});
export const MARK_STYLE = Object.freeze({ targetColor: '#ff2e3c', targetWidth: 5 });
export const MARK_PARTS = Object.freeze([{ part: 'objects:target', label: '対象の印', space: 'board' }]);

/** カードの縦横比（器が板の大きさを決めるのに使う。ここは参考） */
export { CARD_ASPECT };

/** 種類 → 見た目を返す純関数（`core/theme` の `OBJECT_KINDS` ＋ `core/mark` の `MARKS`） */
export default Object.freeze({ card, pile, frame, liveHand, target });
